import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {CallToAction} from 'components/pure/tooltips/CallToAction';
import LoadingOverlay from 'components/pure/utils/LoadingOverlay';
import {Metric, MetricAxis, MetricInfo, MetricParams} from '@store/@types/metric';
import {getIsCoachingZone} from '@store/metrics-trends/metrics-trends.consts';
import {Chart} from 'charts/components/Chart';
import {
  AxisRange,
  COACHING_ZONE,
  margins,
  svgDimensions,
} from 'components/dashboard/manager/nine-box/NineBox.const';
import {NineBoxGrid} from 'components/dashboard/manager/nine-box/NineBoxGrid';
import {NineBoxAxisOptions} from 'components/dashboard/manager/nine-box/NineBoxAxisOptions';
import AxisLabels from './AxisLabels';

const DEF_RANGE: AxisRange = {start: '0', end: '100%'};

export interface NineBoxGridWrapperProps {
  xAxisOptions: MetricInfo[];
  yAxisOptions: MetricInfo[];
  xRange?: AxisRange;
  yRange?: AxisRange;
  isEmpty?: boolean;
  isLoading?: boolean;
  setAxisMetric: (val: {metricAxis: MetricAxis; metricParams: MetricParams}) => void;
  verticalParams: MetricParams;
  horizontalParams: MetricParams;
}

/**
 * Wraps the NineBoxGrid svg and adds x-axis and y-axis metric selectors
 */
export const NineBoxGridWrapper: React.FC<NineBoxGridWrapperProps> = ({
  xAxisOptions,
  yAxisOptions,
  children,
  xRange = DEF_RANGE,
  yRange = DEF_RANGE,
  isEmpty,
  isLoading,
  horizontalParams,
  verticalParams,
  setAxisMetric,
}) => {
  const isCoachingZone = getIsCoachingZone({
    horizontal: horizontalParams,
    vertical: verticalParams,
  });
  const preparedBlockNames = isCoachingZone ? COACHING_ZONE.blockNames : undefined;
  const preparedTitle = isCoachingZone ? COACHING_ZONE.title : undefined;

  // Kpi Goals are rendered left of the chart, so we make a little room for that here
  const verticalMargin = verticalParams.metric === Metric.KPI_OBSERVATION ? 60 : 30;

  const {chart, emptyStyle, root, row} = useStyles();

  return (
    <div className={root}>
      <div className={row}>
        <div className={chart} style={{marginLeft: verticalMargin}}>
          <Chart bordered={false} margins={margins} svgDimensions={svgDimensions}>
            <NineBoxGrid blockNames={preparedBlockNames} title={preparedTitle} />
            {children}
            <AxisLabels xRange={xRange} yRange={yRange} />
          </Chart>
          {isLoading && <LoadingOverlay />}
          {isEmpty && !isLoading && (
            <div className={emptyStyle}>
              <CallToAction text="No data matching the selected filters. Try selecting other metrics, changing the coach's affiliation tab, or changing filters at the top of the page (e.g. date range)" />
            </div>
          )}
        </div>
        <NineBoxAxisOptions
          options={yAxisOptions}
          setAxisMetric={setAxisMetric}
          metricAxis={MetricAxis.VERTICAL}
          metricParams={verticalParams}
        />
      </div>
      <NineBoxAxisOptions
        options={xAxisOptions}
        setAxisMetric={setAxisMetric}
        metricAxis={MetricAxis.HORIZONTAL}
        metricParams={horizontalParams}
      />
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  axisLabel: {
    color: '#808080',
    fontSize: '12px',
  },
  chart: {
    flex: '1',
    margin: '0 0 30px 30px',
    transition: 'margin-left 500ms',
  },
  emptyStyle: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    height: '100%',
    left: 0,
    position: 'absolute',
    top: 0,
    width: '100%',
  },
  root: {
    display: 'flex',
    flexFlow: 'column nowrap',
    padding: '32px 40px 17px 22px',
    position: 'relative',
  },
  row: {
    display: 'flex',
    flexFlow: 'row-reverse nowrap',
  },
}));
